import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, ThumbsUp, ThumbsDown, CheckCircle, MapPin, Award, Briefcase } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { supabase } from '../../lib/supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

async function authHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' };
}

/**
 * LinkedInLeadsView - the recruiter-facing output of a scouting run
 * (backend/routes/linkedin-scout.js). Purely a review surface: good/bad
 * feedback and a "mark contacted" toggle are the only writes this page
 * makes, both recorded for the recruiter's own tracking - nothing here ever
 * contacts a lead on the recruiter's behalf.
 */
const LinkedInLeadsView = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const headers = await authHeaders();
      const response = await fetch(`${API_URL}/linkedin-scout/jobs/${jobId}/leads`, { headers });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to load leads');
      }

      setJob(result.job);
      setLeads(result.leads);
    } catch (err) {
      console.error('Error fetching LinkedIn leads:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const updateLead = async (leadId, body) => {
    try {
      const headers = await authHeaders();
      const response = await fetch(`${API_URL}/linkedin-scout/leads/${leadId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(body),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.error || 'Failed to update lead');

      setLeads((prev) => prev.map((l) => (l.id === leadId ? result.lead : l)));
    } catch (err) {
      console.error('Error updating lead:', err);
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="text-center py-12">
        <Alert className="max-w-2xl mx-auto bg-red-50 border-red-200">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">Error loading leads: {error || 'Not found'}</AlertDescription>
        </Alert>
        <Button onClick={() => navigate(-1)} className="mt-4">Back</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">LinkedIn Leads</h1>
          <p className="text-muted-foreground mt-1">
            {job.rfq?.ref_code} - {job.rfq?.title} &middot; {leads.length} found, sorted by fit
          </p>
        </div>
      </div>

      <Alert className="bg-blue-50 border-blue-200">
        <AlertDescription className="text-blue-800 text-sm">
          Read-only results - nobody below has been contacted. Mark feedback to help tune future
          searches, and "Mark contacted" once you've reached out yourself.
        </AlertDescription>
      </Alert>

      {leads.length === 0 ? (
        <p className="text-sm text-muted-foreground">No leads on this run.</p>
      ) : (
        <div className="space-y-3">
          {leads.map((lead) => (
            <Card key={lead.id} className={lead.contacted_at ? 'opacity-60' : ''}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-4">
                  <CardTitle className="text-base">
                    {lead.linkedin_url ? (
                      <a href={lead.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                        {lead.full_name || 'Unknown name'}
                      </a>
                    ) : (
                      lead.full_name || 'Unknown name'
                    )}
                  </CardTitle>
                  <div className="flex items-center gap-2 shrink-0">
                    {Number.isInteger(lead.fit_rank) && (
                      <Badge variant="outline">Fit #{lead.fit_rank}</Badge>
                    )}
                    {lead.contacted_at && (
                      <Badge className="bg-green-100 text-green-700 border-green-200">
                        <CheckCircle className="h-3 w-3 mr-1" /> Contacted
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-2 text-sm">
                {lead.location && (
                  <p className="flex items-center gap-1.5 text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" /> {lead.location}
                  </p>
                )}
                {Array.isArray(lead.skills_found) && lead.skills_found.length > 0 && (
                  <p className="flex items-start gap-1.5">
                    <Award className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                    <span>{lead.skills_found.join(', ')}</span>
                  </p>
                )}
                {Array.isArray(lead.last_roles) && lead.last_roles.length > 0 && (
                  <p className="flex items-start gap-1.5">
                    <Briefcase className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                    <span>{lead.last_roles.map((r) => [r.title, r.company].filter(Boolean).join(' at ')).join(', ')}</span>
                  </p>
                )}
                {Array.isArray(lead.certifications) && lead.certifications.length > 0 && (
                  <p className="text-muted-foreground">Certifications: {lead.certifications.join(', ')}</p>
                )}
                {lead.fit_notes && <p className="text-muted-foreground italic">{lead.fit_notes}</p>}

                <div className="flex items-center gap-2 pt-2">
                  <Button
                    size="sm"
                    variant={lead.recruiter_feedback === 'good' ? 'default' : 'outline'}
                    onClick={() => updateLead(lead.id, { recruiterFeedback: 'good' })}
                  >
                    <ThumbsUp className="h-3.5 w-3.5 mr-1" /> Good
                  </Button>
                  <Button
                    size="sm"
                    variant={lead.recruiter_feedback === 'bad' ? 'default' : 'outline'}
                    onClick={() => updateLead(lead.id, { recruiterFeedback: 'bad' })}
                  >
                    <ThumbsDown className="h-3.5 w-3.5 mr-1" /> Bad
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateLead(lead.id, { contacted: !lead.contacted_at })}
                  >
                    {lead.contacted_at ? 'Unmark contacted' : 'Mark contacted'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default LinkedInLeadsView;
